/**
 * Provenance and platform-default formatting for the General tab (design §17.1, §17.1.6).
 *
 * Two pure functions:
 *  - {@link generalFieldProvenance} — given a field's draft/loaded/platform-default values and
 *    whether a reset is pending, decides which of the four General-tab states (design §17.1.2's
 *    table) applies. Channel-tab provenance stays in `channelOverrideController.ts` (it only ever
 *    needs `OVERRIDE` / `GENERAL` / `DEFAULT`, already derivable from `overriddenFields` /
 *    `defaultedFields` with no extra state machine).
 *  - {@link formatSettingValue} — renders a field's value exactly as its own control would, for the
 *    "Platform default: {value}" hint. Never used for the editable control itself.
 */
import type { TFunction } from 'i18next';

import { getIntlLocale } from '@/shared/i18n';

import { formatMinutesEquivalent } from './format';
import { templateLabelSegment } from './templateLabels';
import type {
  ChatbotAfterHoursPolicy,
  ChatbotAiMode,
  ChatbotDictionaries,
  ChatbotGreetingFlow,
  ChatbotLanguagePolicy,
  ChatbotSettingsFields,
} from './types';
import { hasWorkingHoursRestriction } from './workingHours';

export type GeneralFieldProvenance =
  /** In `defaultedFields` and unedited — chip "Platform default", no hint, no row action. */
  | { kind: 'DEFAULT' }
  /** "Use platform default" clicked on a saved value — dashed chip, "Keep your value" action. */
  | { kind: 'DEFAULT_PENDING' }
  /** Merchant-set, equal to the platform default — hint "Same as the platform default". */
  | { kind: 'SAME_AS_DEFAULT' }
  /** Merchant-set (or edited away from a default), differs from the platform default. */
  | { kind: 'SET' };

/**
 * `wasDefaulted` = the field's name is in the general row's `defaultedFields[]` as last loaded from
 * the server (i.e. the tenant has never explicitly saved it). `isDirty` = the draft differs from
 * that same loaded value (an in-progress edit, uncommitted). `pending` = the field is in the page's
 * `pendingResets` set (an uncommitted "Use platform default").
 */
export function generalFieldProvenance(
  wasDefaulted: boolean,
  isDirty: boolean,
  isEqualToPlatformDefault: boolean,
  pending: boolean,
): GeneralFieldProvenance {
  if (pending) return { kind: 'DEFAULT_PENDING' };
  if (wasDefaulted && !isDirty) return { kind: 'DEFAULT' };
  if (isEqualToPlatformDefault) return { kind: 'SAME_AS_DEFAULT' };
  return { kind: 'SET' };
}

export interface FormatValueContext {
  t: TFunction;
  dictionaries?: ChatbotDictionaries;
}

const GREETING_FLOW_OPTION_KEYS: Record<ChatbotGreetingFlow, string> = {
  SIMPLE: 'behaviour.greetingFlow.options.simple',
  REGISTRATION_FIRST: 'behaviour.greetingFlow.options.registrationFirst',
};
const AI_MODE_OPTION_KEYS: Record<ChatbotAiMode, string> = {
  OFF: 'escalation.aiMode.options.off',
  CLASSIFY_ONLY: 'escalation.aiMode.options.classifyOnly',
  ASSIST: 'escalation.aiMode.options.assist',
};
const AFTER_HOURS_OPTION_KEYS: Record<ChatbotAfterHoursPolicy, string> = {
  BOT_ONLY: 'escalation.afterHours.options.botOnly',
  QUEUE: 'escalation.afterHours.options.queue',
  TEMPLATE: 'escalation.afterHours.options.template',
};
const LANGUAGE_POLICY_OPTION_KEYS: Record<ChatbotLanguagePolicy, string> = {
  CUSTOMER: 'behaviour.language.options.customer',
  TENANT_DEFAULT: 'behaviour.language.options.tenantDefault',
};

function formatCommandList(codes: string[], ctx: FormatValueContext): string {
  const allCodes = ctx.dictionaries?.commands.map((c) => c.code);
  if (allCodes && allCodes.length > 0 && allCodes.every((code) => codes.includes(code))) {
    return ctx.t('provenance.values.allCommands');
  }
  return codes.map((code) => `/${code}`).join(', ');
}

function intentLabel(code: string, ctx: FormatValueContext): string {
  const known = ctx.dictionaries?.intents.some((intent) => intent.code === code);
  return known ? ctx.t(`escalation.intents.${code}`) : code;
}

function toolLabel(name: string, ctx: FormatValueContext): string {
  const known = ctx.dictionaries?.tools.some((tool) => tool.name === name);
  return known ? ctx.t(`escalation.tools.items.${templateLabelSegment(name)}`, name) : name;
}

/**
 * Renders `value` exactly as the field's own control reads it (design §17.1.6's table) — feeds the
 * `Platform default: {value}` hint and nothing else (never the editable control).
 */
export function formatSettingValue<K extends keyof ChatbotSettingsFields>(
  field: K,
  value: ChatbotSettingsFields[K],
  ctx: FormatValueContext,
): string {
  const { t } = ctx;
  switch (field) {
    case 'greetingFlow':
      return t(GREETING_FLOW_OPTION_KEYS[value as ChatbotGreetingFlow]);
    case 'aiMode':
      return t(AI_MODE_OPTION_KEYS[value as ChatbotAiMode]);
    case 'afterHoursPolicy':
      return t(AFTER_HOURS_OPTION_KEYS[value as ChatbotAfterHoursPolicy]);
    case 'languagePolicy':
      return t(LANGUAGE_POLICY_OPTION_KEYS[value as ChatbotLanguagePolicy]);
    case 'handoffEnabled':
    case 'quoteInboundInReplies':
      return value ? t('availability.state.on') : t('availability.state.off');
    case 'confidenceThreshold':
      return new Intl.NumberFormat(getIntlLocale(), { maximumFractionDigits: 2 }).format(
        value as number,
      );
    case 'lowConfidenceMaxStreak':
    case 'loopMaxUnresolvedTurns':
      return String(value);
    case 'interimAckAfterMs':
    case 'agentDeadlineMs':
      return `${Math.round((value as number) / 1000)} ${t('units.seconds')}`;
    case 'episodeIdleTimeoutMinutes':
    case 'idleGraceMinutes':
    case 'escalationQueueTimeoutMinutes':
      return formatMinutesEquivalent(value as number, t);
    case 'identityNegativeCacheTtlHours':
      return `${value} ${t('units.hours')}`;
    case 'unsupportedReplyCooldownSeconds':
      return `${value} ${t('units.seconds')}`;
    case 'closeAfterIdleDays':
    case 'retentionDays':
      return `${value} ${t('units.days')}`;
    case 'deliveryMaxRedispatch':
      return `${value} ${t('units.attempts')}`;
    case 'enabledCommands':
      return formatCommandList(value as string[], ctx);
    case 'escalationPolicyTopics': {
      const topics = value as string[];
      return topics.length === 0
        ? t('provenance.values.none')
        : topics.map((code) => intentLabel(code, ctx)).join(', ');
    }
    case 'toolAllowlist': {
      const tools = value as string[];
      return tools.length === 0
        ? t('provenance.values.allTools')
        : tools.map((name) => toolLabel(name, ctx)).join(', ');
    }
    case 'workingHours':
      return hasWorkingHoursRestriction(value as Record<string, unknown> | null)
        ? '' // WorkingHoursField renders its own provenance — never reached in practice (§17.1.6)
        : t('provenance.values.noRestriction');
    default:
      return String(value);
  }
}
